/* eslint-disable no-unused-expressions */
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
// ════════════════════════════════════════════════════════════════
// PainLocationScreen.jsx (Conversational)
// Body diagram with tappable zone buttons + canvas highlight overlay.
// Ports prototype's showPainLocation(), toggleZone(), drawHighlights().
// Converted: pushes Myra message on mount, returns inlineUi only,
// pushes text-only frozen widget + user echo on completion.
// ════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useScreenVoice } from "./useScreenVoice";
import { ZONE_COORDS, SKELETON, hlType, splitHalf, edgeSide, hasLegPain, CLINICAL_PATHS, PRIMARY_ZONE_DEFAULTS, HQ_ZONES } from "./GSA_CONSTANTS";
import { MYRA_MESSAGES } from "./GSA_MYRA_MESSAGES";
import { generatePayloadForScreen } from "./generatePayloadForScreen";
import { AnalyticsService } from "../self-assesment/SelfAssesment";
import styles from "./SelfAssessment.module.css";
import MyraChatService from "../voice-agent/MyraChatService";
import MyraInteraction from "../self-assesment/MyraInteractionController";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";

let _plId = 90000;
function nextPlId() {
  return ++_plId;
}

export default function PainLocationScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, readOnly = false, entry, phase = "problem_area", persistAndNavigate, paCommandRef, detectedArea }) {
  const [internalPhase, setInternalPhase] = useState(phase);

  const [problemArea, setProblemArea] = useState(phase === "pain_location" ? assessmentData.problemArea : null);
  const coords = problemArea ? ZONE_COORDS[problemArea] : null;
  const leftZones = coords ? Object.keys(coords.left) : [];
  const rightZones = coords ? Object.keys(coords.right) : [];
  const initialPrimary = readOnly && entry?.data?.primaryZone ? entry.data.primaryZone : phase === "pain_location" ? assessmentData.primaryZone || null : null;
  const initialSecondaries = readOnly && entry?.data?.secondaryZones ? new Set(entry.data.secondaryZones) : phase === "pain_location" && assessmentData.secondaryZones ? new Set(assessmentData.secondaryZones) : new Set();

  const [primaryZone, setPrimaryZone] = useState(initialPrimary);
  const [secondaryZones, setSecondaryZones] = useState(initialSecondaries);

  // ── Age confirm (gates Continue, enabled once Phase 2 begins) ──
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageHighlight, setAgeHighlight] = useState(false);

  // ── UI flags ──
  const [ready, setReady] = useState(Boolean(readOnly && entry));
  const [locked, setLocked] = useState(false);

  // ── Refs ──
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const wrapRef = useRef(null);
  const myraEntryIdRef = useRef(null);

  const isVoiceMode = MyraInteraction.getMode() === "voice";

  // ── Push divider + Myra message on mount ──
  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (!readOnly && phase === "problem_area") {
        // Phase 1 mount only — Phase 2 component instance is the same React node, not a fresh mount
        const promptText = MYRA_MESSAGES.problemArea?.prompt || MYRA_MESSAGES.painLocation?.prompt || "Can you start by telling me where you are feeling pain?";
        const myraEntry = await showMyraMessage(promptText, 800);
        if (myraEntry?.id) myraEntryIdRef.current = myraEntry.id;
        // Activate phraseStart by default
        requestAnimationFrame(() => setPromptPhrase("start"));
      }
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Read-only mode (history scrollback) ──
  useEffect(() => {
    if (readOnly) {
      setReady(true);
    }
  }, [readOnly, entry]);

  const setPromptPhrase = useCallback((which) => {
    // which ∈ "start" | "add" | "none"
    const spans = document.querySelectorAll(".promptPhrase");
    spans.forEach((s) => {
      const phrase = s.getAttribute("data-phrase");
      if (which === "none") {
        s.classList.remove("active");
      } else if (phrase === which) {
        s.classList.add("active");
      } else {
        s.classList.remove("active");
      }
    });
  }, []);
  useEffect(() => {
    if (!paCommandRef) return;
    paCommandRef.current = (areaId) => {
      if (locked || internalPhase !== "problem_area") return;
      handleRegionTap(areaId, "left");
    };
    return () => {
      if (paCommandRef) paCommandRef.current = null;
    };
  }, [locked, internalPhase]);

  useEffect(() => {
    if (detectedArea && internalPhase === "problem_area" && !locked && ready) {
      handleRegionTap(detectedArea, "left");
    }
  }, [detectedArea, ready]);
  const handleRegionTap = useCallback(
    async (areaId, side) => {
      if (locked || internalPhase !== "problem_area") return;

      setProblemArea(areaId);
      setInternalPhase("pain_location");

      const defaultZone = PRIMARY_ZONE_DEFAULTS[areaId];
      if (defaultZone) {
        setPrimaryZone(`${side}:${defaultZone}`);
      }
      setSecondaryZones(new Set());

      setPromptPhrase("add");

      if (persistAndNavigate) {
        persistAndNavigate(
          {
            problemArea: areaId,
            selected_problem_area: CLINICAL_PATHS[areaId]?.name || areaId,
            EngagedOnLanding: window.EngagedOnLanding || false,
          },
          "pain_location",
        );
      }

      // 5. Manually swap the midjourney payload Claude sees:
      //    Phase 1 sent the "problem_area" payload; Phase 2 sends "pain_location".
      //    This mirrors what the old standalone screens did via useScreenVoice.
      if (window.mayraCode) {
        const newPayload = generatePayloadForScreen("pain_location", {
          ...assessmentData,
          problemArea: areaId,
          selected_problem_area: CLINICAL_PATHS[areaId]?.name || areaId,
        });
        window.mayraForwardCode = { midjourneyData: newPayload };
        try {
          VoiceService.sendMidJourneyData(newPayload);
        } catch (e) {
          console.warn("[Combined PL] sendMidJourneyData on phase transition failed:", e);
        }
      }

      if (window.screenTracker) {
        try {
          window.screenTracker.exitScreen?.("problem_area");
          window.screenTracker.enterScreen?.("pain_location");
        } catch (e) {
          console.warn("[Combined PL] screenTracker swap failed:", e);
        }
      }

      try {
        MyraInteraction.onScreenEnter?.("pain_location");
      } catch (e) {
        console.warn("[Combined PL] MyraInteraction.onScreenEnter failed:", e);
      }
    },
    [locked, internalPhase, assessmentData, persistAndNavigate],
  );
  // ── Voice ──
  const validateAndContinue = useCallback(() => {
    if (internalPhase !== "pain_location" || !primaryZone) {
      if (isVoiceMode && VoiceService) {
        VoiceService.doSay(MYRA_MESSAGES.painLocation?.noSelectionPrompt || "Please select at least one area where you feel pain before we continue.");
      }
      return;
    }
    if (!ageConfirmed) {
      if (isVoiceMode && VoiceService) {
        VoiceService.doSay("Please confirm your age before we continue.");
      }
      setAgeHighlight(true);
      setTimeout(() => setAgeHighlight(false), 2000);
      return;
    }
    handleContinue();
  }, [internalPhase, primaryZone, ageConfirmed]);

  async function handleContinue() {
    if (locked || readOnly) return;
    setLocked(true);

    // Build selectedZones (legacy contract — backend + downstream consumers)
    const secondariesArray = [...secondaryZones];
    const selectedZonesArray = [primaryZone, ...secondariesArray];
    const isLegPain = hasLegPain(selectedZonesArray);

    // Echo text
    const formatKey = (k) => {
      const [s, z] = k.split(":");
      return `${s === "left" ? "Left" : "Right"} ${z}`;
    };
    const primaryLabel = formatKey(primaryZone);
    const secondaryCount = secondariesArray.length;
    const echoText = secondaryCount === 0 ? primaryLabel : `${primaryLabel} + ${secondaryCount} other ${secondaryCount === 1 ? "area" : "areas"}`;

    // Frozen widget — uses existing pain_location_text widget type with a richer zoneText
    const zoneText = secondaryCount === 0 ? `${primaryLabel} (primary)` : `${primaryLabel} (primary), ${secondariesArray.map(formatKey).join(", ")}`;

    pushToHistory(
      {
        type: "pain-location-summary",
        id: nextPlId(),
        data: {
          selectedZones: selectedZonesArray,
          primaryZone,
          secondaryZones: secondariesArray,
          problemArea,
          zoneText,
        },
      },
      { type: "user", text: echoText, id: nextPlId() },
    );

    // Deactivate prompt phrases (history bubble stays visible without highlights)
    setPromptPhrase("none");

    // Myra transition message — keep existing copy patterns
    const transitionMsg = secondaryCount === 0 ? `${primaryLabel} — got it. Now I need to do a quick safety check before we continue.` : `Got it — primary at ${primaryLabel}, plus ${secondaryCount} other ${secondaryCount === 1 ? "area" : "areas"}. Now I need to do a quick safety check before we continue.`;
    await showMyraMessage(transitionMsg, 800);

    // Hand off to safety_screening
    onContinue({
      problemArea,
      selected_problem_area: CLINICAL_PATHS[problemArea]?.name || problemArea,
      selectedZones: selectedZonesArray,
      primaryZone,
      secondaryZones: secondariesArray,
      painLocationComplete: true,
      isLegPain,
      selected_pain_location: true,
    });
  }
  useEffect(() => {
    if (readOnly) return;
    if (internalPhase === "problem_area") {
      window.currentFlow = "flow_problem_area";
      if (VoiceService) VoiceService._currentFlow = "flow_problem_area";
      const payload = generatePayloadForScreen("problem_area", assessmentData);
      if (window.mayraCode && VoiceService) {
        window.mayraForwardCode = { midjourneyData: payload };
        try {
          VoiceService.startFlowWithoutAnnouncement("flow_problem_area");
          setTimeout(() => {
            VoiceService.sendMidJourneyData(payload);
          }, 500);
        } catch (e) {
          console.warn("[Combined PL] flow_problem_area setup failed:", e);
        }
      }
      if (!window.trSessionId && AnalyticsService) AnalyticsService.startSession();
      localStorage.setItem("didGsaFlowBefore", "true");
    } else {
      window.currentFlow = "flow_pain_location";
      if (VoiceService) VoiceService._currentFlow = "flow_pain_location";
      const payload = generatePayloadForScreen("pain_location", assessmentData);
      if (window.mayraCode && VoiceService) {
        window.mayraForwardCode = { midjourneyData: payload };
        try {
          VoiceService.startFlowWithoutAnnouncement("flow_pain_location");
          setTimeout(() => {
            VoiceService.sendMidJourneyData(payload);
          }, 500);
        } catch (e) {
          console.warn("[Combined PL] flow_pain_location setup failed:", e);
        }
      }
    }
  }, [phase, internalPhase]);

  // ════════════════════════════════════════════════════════════════
  // Voice: Beacon zone-enum → display name map
  // ════════════════════════════════════════════════════════════════
  const BEACON_ZONE_TO_DISPLAY = {
    LOWER_BACK: "Lower Back",
    BUTTOCK: "Buttock",
    THIGH: "Thigh",
    LOWER_LEG: "Lower Leg",
    FRONT_GROIN: "Front/Groin",
    SIDE_HIP: "Side Hip",
    HIP: "Hip",
    BACK_THIGH: "Back Thigh",
    FRONT_THIGH: "Front Thigh",
    INNER_THIGH: "Inner Thigh",
    OUTER_THIGH: "Outer Thigh",
    KNEE: "Knee",
    FRONT_KNEE: "Front Knee",
    BACK_KNEE: "Back Knee",
    INNER_KNEE: "Inner Knee",
    OUTER_KNEE: "Outer Knee",
    BACK: "Back",
  };

  const SIDE_TOKENS = new Set(["LEFT_SIDE", "RIGHT_SIDE", "LEFT", "RIGHT"]);

  /**
   * Parse a single Beacon action value into { side, zone }.
   *  - "LEFT_SIDE" / "RIGHT_SIDE"  → { side: 'left'|'right', zone: null }
   *  - "LEFT_THIGH" / "RIGHT_THIGH"→ { side: 'left'|'right', zone: 'Thigh' }
   *  - "THIGH"                     → { side: null, zone: 'Thigh' }
   */
  function parseBeaconValue(raw) {
    if (!raw) return { side: null, zone: null };

    // Pure side signals
    if (raw === "LEFT_SIDE" || raw === "LEFT") return { side: "left", zone: null };
    if (raw === "RIGHT_SIDE" || raw === "RIGHT") return { side: "right", zone: null };

    // BOTH_SIDES — emit as no side (caller will apply to both sides)
    if (raw === "BOTH_SIDES") return { side: null, zone: null, bothSides: true };

    // Prefixed: LEFT_<ZONE> or RIGHT_<ZONE>
    if (raw.startsWith("LEFT_")) {
      const zoneKey = raw.slice(5);
      return { side: "left", zone: BEACON_ZONE_TO_DISPLAY[zoneKey] || null };
    }
    if (raw.startsWith("RIGHT_")) {
      const zoneKey = raw.slice(6);
      return { side: "right", zone: BEACON_ZONE_TO_DISPLAY[zoneKey] || null };
    }

    // Bare zone
    return { side: null, zone: BEACON_ZONE_TO_DISPLAY[raw] || null };
  }
  /**
   * Process an array of SELECT_PAIN_ZONE actions sequentially.
   * Maintains pending zone + pending laterality across items.
   * Returns array of { side, zone } pairs to toggle.
   *
   * Rules:
   *  - Zone arrives, no laterality pending  → queue both sides
   *  - Zone arrives, laterality pending     → use that side, clear pending laterality
   *  - Laterality arrives, zone pending     → use that side for pending zone, clear pending zone
   *  - Laterality arrives, no zone pending  → store as pending laterality for next zone
   *  - Zone+side together (prefixed)        → emit immediately
   */
  // function resolveBeaconActions(actionsArray) {
  //   const results = []; // { side, zone }[]
  //   let pendingZone = null; // display name waiting for a side
  //   let pendingLaterality = null; // 'left' | 'right' waiting for a zone

  //   for (const item of actionsArray) {
  //     if (item.action !== "SELECT_PAIN_ZONE") continue;
  //     const { side, zone } = parseBeaconValue(item.value);

  //     if (zone && side) {
  //       // Self-contained (e.g. RIGHT_THIGH) — emit immediately
  //       // Flush any pending zone with both sides first
  //       if (pendingZone) {
  //         results.push({ side: "left", zone: pendingZone });
  //         results.push({ side: "right", zone: pendingZone });
  //         pendingZone = null;
  //       }
  //       results.push({ side, zone });
  //       pendingLaterality = null;
  //     } else if (zone && !side) {
  //       // Bare zone — do we have a pending laterality?
  //       if (pendingLaterality) {
  //         results.push({ side: pendingLaterality, zone });
  //         pendingLaterality = null;
  //       } else {
  //         // No side known yet — store zone, will resolve when side arrives
  //         // (or flush as both sides at end)
  //         pendingZone = zone;
  //       }
  //     } else if (side && !zone) {
  //       // Pure laterality signal
  //       if (pendingZone) {
  //         // Zone was waiting — resolve it now
  //         results.push({ side, zone: pendingZone });
  //         pendingZone = null;
  //       } else {
  //         // No zone yet — store laterality for next incoming zone
  //         pendingLaterality = side;
  //       }
  //     }
  //   }

  //   // Flush any remaining pending zone as both sides
  //   if (pendingZone) {
  //     results.push({ side: "left", zone: pendingZone });
  //     results.push({ side: "right", zone: pendingZone });
  //   }

  //   return results;
  // }
  function resolveBeaconActions(actionsArray) {
    const results = [];

    // Use an array queue instead of a single slot
    // Each entry: { zone, side } where side may be null (= both)
    const pendingZones = []; // zones waiting for a laterality
    let pendingLaterality = null; // a side waiting for a zone

    function flushPendingZones() {
      // Any zone still in queue with no side → emit both sides
      pendingZones.forEach(({ zone }) => {
        results.push({ side: "left", zone });
        results.push({ side: "right", zone });
      });
      pendingZones.length = 0;
    }

    for (const item of actionsArray) {
      if (item.action !== "SELECT_PAIN_ZONE") continue;
      const { side, zone } = parseBeaconValue(item.value);

      if (zone && side) {
        // ── Self-contained prefixed value e.g. RIGHT_THIGH ──
        // Flush any pending zones as both-sides first
        flushPendingZones();
        pendingLaterality = null;
        results.push({ side, zone });
      } else if (zone && !side) {
        // ── Bare zone, no side embedded ──
        if (pendingLaterality) {
          // A laterality was waiting → pair them immediately
          results.push({ side: pendingLaterality, zone });
          pendingLaterality = null;
          // Do NOT flush pendingZones here — they may still get a laterality
          // Actually: pending zones had no laterality, this new zone consumed the laterality.
          // Flush pending zones as both-sides since their laterality window has passed.
          flushPendingZones();
        } else {
          // No laterality available → push to queue, will be flushed as both-sides
          // unless a laterality arrives next
          // But first: flush existing queue since a new zone without laterality
          // means the queued zones are also without laterality — emit them now
          // so they don't get accidentally paired with a future laterality
          flushPendingZones();
          pendingZones.push({ zone });
        }
      } else if (side && !zone) {
        // ── Pure laterality signal ──
        if (pendingZones.length > 0) {
          // Apply laterality to the LAST pending zone only
          // Earlier ones in queue are already "orphaned" → flush them as both-sides
          const lastZone = pendingZones.pop(); // take the most recent
          // flush the rest as both sides
          flushPendingZones();
          results.push({ side, zone: lastZone.zone });
        } else {
          // No pending zone → store laterality for next incoming zone
          pendingLaterality = side;
        }
      }
    }

    // End: flush any remaining queued zones as both sides
    flushPendingZones();

    return results;
  }

  useScreenVoice({
    screenName: internalPhase === "problem_area" ? "problem_area" : "pain_location",
    flowName: internalPhase === "problem_area" ? "flow_problem_area" : "flow_pain_location",
    payloadKey: internalPhase === "problem_area" ? "problem_area" : "pain_location",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        // Phase 1 — region selection via voice
        if (internalPhase === "problem_area") {
          if (extracted && extracted.problemArea) {
            handleRegionTap(extracted.problemArea, "left");
          }
          if (value === "continue_command") {
            // Voice user said "continue" before picking a region — gentle reminder
            if (isVoiceMode && VoiceService) {
              VoiceService.doSay(MYRA_MESSAGES.problemArea?.noSelectionPrompt || "Please make your selection before we can move to the next screen.");
            }
          }
          return;
        }

        // Phase 2 — zone selection via voice
        if (value === "continue_command") {
          validateAndContinue();
          return;
        }
        if (extracted && extracted.selectedZones) {
          // Apply incoming zones: first → primary, rest → secondary
          const zonesArray = Array.isArray(extracted.selectedZones) ? extracted.selectedZones : [extracted.selectedZones];
          if (zonesArray.length > 0) {
            setPrimaryZone(zonesArray[0]);
            setSecondaryZones(new Set(zonesArray.slice(1)));
          }
        }
      },
      MOVE_ON: () => {
        if (internalPhase === "problem_area") {
          // Voice "next" with no region picked — gentle reminder
          if (isVoiceMode && VoiceService) {
            VoiceService.doSay(MYRA_MESSAGES.problemArea?.noSelectionPrompt || "Please make your selection before we can move to the next screen.");
          }
        } else {
          validateAndContinue();
        }
      },
      SELECT_PROBLEM_AREA: (value, speech, intent, extracted, actionValue, matched) => {
        console.log("SELECT_PROBLEM_AREA triggered with:", { value, speech, intent, extracted, actionValue });
        const ValidAreas = ["LBP", "HIP", "THIGH", "KNEE"];
        let valueToCheck = value || actionValue;
        if (valueToCheck && ValidAreas.includes(valueToCheck)) {
          const side = "left";
          setProblemArea(valueToCheck);
          setInternalPhase("pain_location");
          const defaultZone = PRIMARY_ZONE_DEFAULTS[valueToCheck];
          if (defaultZone) {
            setPrimaryZone(`${side}:${defaultZone}`);
          }
          setSecondaryZones(new Set());
          setPromptPhrase("add");
          if (persistAndNavigate) {
            persistAndNavigate(
              {
                problemArea: valueToCheck,
                selected_problem_area: CLINICAL_PATHS[valueToCheck]?.name || valueToCheck,
                EngagedOnLanding: window.EngagedOnLanding || false,
              },
              "pain_location",
            );
          }
        }
      },
      SELECT_PAIN_ZONE: (value, speech, intent, extracted, actionValue, actions, matched) => {
        console.log("[VOICE] SELECT_PAIN_ZONE triggered with:", { value, speech, intent, extracted, actionValue, actions });

        if (matched === "classifier") {
          // Handle single zone from classifier
          const zoneValue = value || actionValue;
          if (!zoneValue) return;

          // Parse the single zone value
          const { side, zone } = parseBeaconValue(zoneValue);

          // Get valid zone names for current problemArea
          const validLeft = coords ? Object.keys(coords.left) : [];
          const validRight = coords ? Object.keys(coords.right) : [];

          // If no side specified, apply to BOTH sides (bilateral pain)
          if (!side) {
            // Apply to left side if zone exists there
            if (validLeft.includes(zone)) {
              handleZoneTap("left", zone);
            }
            // Apply to right side if zone exists there
            if (validRight.includes(zone)) {
              handleZoneTap("right", zone);
            }
            // If zone doesn't exist on either side, log error
            if (!validLeft.includes(zone) && !validRight.includes(zone)) {
              console.log(`[VOICE] Zone "${zone}" not found in left or right for ${problemArea}`);
            }
          } else {
            // Side specified, apply to that side only
            const validSet = side === "left" ? validLeft : validRight;
            if (!validSet.includes(zone)) {
              console.log(`[VOICE] Ignoring unknown zone for ${problemArea}: ${side}:${zone}`);
              return;
            }
            handleZoneTap(side, zone);
          }
        } else {
          // `actions` is the full actions[] array from the NLU payload
          const actionsArray = Array.isArray(actions) ? actions : [];
          if (actionsArray.length === 0) return;

          // Resolve sequential zone+side pairs
          const pairs = resolveBeaconActions(actionsArray);
          console.log("[VOICE] Resolved pairs:", pairs);

          // Get valid zone names for current problemArea
          const validLeft = coords ? Object.keys(coords.left) : [];
          const validRight = coords ? Object.keys(coords.right) : [];

          pairs.forEach(({ side, zone }) => {
            // Guard: zone must exist in this problemArea's coords
            const validSet = side === "left" ? validLeft : validRight;
            if (!validSet.includes(zone)) {
              console.log(`[VOICE] Ignoring unknown zone for ${problemArea}: ${side}:${zone}`);
              return;
            }
            handleZoneTap(side, zone);
          });
        }
      },
    },
    exitReason: internalPhase === "problem_area" ? "problem_area_selected" : "pain_location_completed",
  });
  function paintZone(ctx, key, coords, offX, offY, imgW, imgH, kind) {
    const [side, zone] = key.split(":");
    const c = coords[side]?.[zone];
    if (!c) return;

    const x = offX + (c[0] / 100) * imgW;
    const y = offY + (c[1] / 100) * imgH;
    const w = (c[2] / 100) * imgW;
    const h = (c[3] / 100) * imgH;
    const type = hlType(zone);

    const fill = kind === "primary" ? "rgba(13,148,136,0.50)" : "rgba(220,38,38,0.35)";
    const stroke = kind === "primary" ? "rgba(13,148,136,0.90)" : "rgba(220,38,38,0.70)";

    if (type === "full") {
      ctx.fillStyle = fill;
      roundRect(ctx, x, y, w, h, Math.min(6, w / 2, h / 2));
      ctx.fill();
      if (kind === "primary") {
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }
    } else if (type === "split") {
      ctx.fillStyle = fill;
      const half = splitHalf(zone);
      const drawY = half === "upper" ? y : y + h / 2;
      const drawH = h / 2;
      roundRect(ctx, x, drawY, w, drawH, Math.min(4, w / 2, drawH / 2));
      ctx.fill();
      if (kind === "primary") {
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }
    } else if (type === "edge") {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = kind === "primary" ? 5 : 3.5;
      ctx.lineCap = "round";
      const eSide = edgeSide(zone);
      let lineX;
      if (side === "left") {
        lineX = eSide === "outer" ? x : x + w;
      } else {
        lineX = eSide === "outer" ? x + w : x;
      }
      ctx.beginPath();
      ctx.moveTo(lineX, y);
      ctx.lineTo(lineX, y + h);
      ctx.stroke();
    }
  }
  // ── Canvas drawing ──
  const drawHighlights = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !img || !wrap) return;

    const wrapRect = wrap.getBoundingClientRect();
    canvas.width = wrapRect.width;
    canvas.height = wrapRect.height;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!coords) return; // Phase 1: no highlights yet

    const imgRect = img.getBoundingClientRect();
    const offX = imgRect.left - wrapRect.left;
    const offY = imgRect.top - wrapRect.top;
    const imgW = imgRect.width;
    const imgH = imgRect.height;

    // Draw secondaries first (red), then primary on top (teal)
    secondaryZones.forEach((key) => paintZone(ctx, key, coords, offX, offY, imgW, imgH, "secondary"));
    if (primaryZone) {
      paintZone(ctx, primaryZone, coords, offX, offY, imgW, imgH, "primary");
    }
  }, [primaryZone, secondaryZones, coords]);

  // Redraw on selection change
  useEffect(() => {
    drawHighlights();
  }, [drawHighlights]);

  // ResizeObserver for canvas redraw
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !window.ResizeObserver) return;
    const ro = new ResizeObserver(() => drawHighlights());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [drawHighlights]);

  // ── Zone toggle ──
  const handleZoneTap = useCallback(
    (side, zone) => {
      if (locked || readOnly) return;
      const key = `${side}:${zone}`;

      if (primaryZone === key) {
        // User tapped the primary — try promotion first, else reset
        const hq = HQ_ZONES[problemArea] || [];
        let promoted = null;
        for (const candidateKey of secondaryZones) {
          const [candSide, candZone] = candidateKey.split(":");
          if (candSide === side && hq.includes(candZone)) {
            promoted = candidateKey;
            break;
          }
        }
        if (promoted) {
          // Promote
          setSecondaryZones((prev) => {
            const next = new Set(prev);
            next.delete(promoted);
            return next;
          });
          setPrimaryZone(promoted);
          // Phase stays 2; prompt phrase stays on "add"
        } else {
          // Full reset → back to Phase 1
          setProblemArea(null);
          setPrimaryZone(null);
          setSecondaryZones(new Set());
          setInternalPhase("problem_area");
          setPromptPhrase("start");
          // Note: we do NOT reverse the persistAndNavigate from Phase 1 → Phase 2.
          // The shell's currentStep stays "pain_location" but the visible screen
          // is back to region buttons. On the next region tap, the persistAndNavigate
          // call in handleRegionTap is a no-op for currentStep (already "pain_location")
          // but still fires AnalyticsService.trackStep, which is harmless.
          // assessmentData.problemArea is also left set; it gets overwritten on the
          // next region tap.
        }
        return;
      }

      // Tapped a non-primary zone — toggle as secondary
      setSecondaryZones((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [locked, readOnly, primaryZone, secondaryZones, problemArea, setPromptPhrase],
  );
  const helperVisible = (() => {
    if (internalPhase !== "pain_location" || !primaryZone) return false;
    const [primarySide] = primaryZone.split(":");
    const hq = HQ_ZONES[problemArea] || [];
    for (const key of secondaryZones) {
      const [side, zone] = key.split(":");
      if (side === primarySide && hq.includes(zone)) return false;
    }
    return true;
  })();

  // ── No coords fallback ──
  if (!coords && internalPhase === "pain_location") {
    return (
      <div className={styles.inlineUi}>
        <p>No zone data for this body area.</p>
        <button
          className={styles.btnSafe}
          onClick={() =>
            onContinue({
              selectedZones: [],
              primaryZone: null,
              secondaryZones: [],
              painLocationComplete: true,
              isLegPain: false,
            })
          }
        >
          Continue
        </button>
      </div>
    );
  }

  if (!ready) return null;

  // Phase 1 region buttons (4 per side)
  const REGIONS = [
    { id: "LBP", label: "Lower Back" },
    { id: "HIP", label: "Hip" },
    { id: "THIGH", label: "Thigh" },
    { id: "KNEE", label: "Knee" },
  ];

  const canContinue = internalPhase === "pain_location" && primaryZone && ageConfirmed;
  const ageEnabled = internalPhase === "pain_location";

  return (
    <div className={styles.inlineUi}>
      {!locked ? (
        <>
          <div className={styles.painLocationWrap}>
            {/* Side labels */}
            <div className={styles.sideLabels}>
              <span className={styles.sideLabel}>LEFT</span>
              <span className={styles.sideLabelSpacer} />
              <span className={styles.sideLabel}>RIGHT</span>
            </div>

            {/* Body section: left buttons | image+canvas | right buttons */}
            <div className={styles.bodySection}>
              {/* LEFT button column */}
              <div className={styles.btnCol}>
                {internalPhase === "problem_area"
                  ? REGIONS.map((r) => (
                      <button key={`left:${r.id}`} className={styles.zoneBtn} onClick={() => !readOnly && handleRegionTap(r.id, "left")}>
                        {r.label}
                      </button>
                    ))
                  : leftZones.map((z) => {
                      const key = `left:${z}`;
                      const isPrimary = primaryZone === key;
                      const isSecondary = secondaryZones.has(key);
                      const cls = `${styles.zoneBtn} ${isPrimary ? styles.primary : ""} ${isSecondary ? styles.secondary : ""}`;
                      return (
                        <button key={key} className={cls} onClick={() => handleZoneTap("left", z)}>
                          {z}
                        </button>
                      );
                    })}
              </div>

              {/* Body image + canvas */}
              <div className={styles.bodyWrap} ref={wrapRef}>
                <img ref={imgRef} src={SKELETON} alt="Body outline" onLoad={drawHighlights} />
                <canvas ref={canvasRef} className={styles.hlCanvas} />
              </div>

              {/* RIGHT button column */}
              <div className={styles.btnCol}>
                {internalPhase === "problem_area"
                  ? REGIONS.map((r) => (
                      <button key={`right:${r.id}`} className={styles.zoneBtn} onClick={() => !readOnly && handleRegionTap(r.id, "right")}>
                        {r.label}
                      </button>
                    ))
                  : rightZones.map((z) => {
                      const key = `right:${z}`;
                      const isPrimary = primaryZone === key;
                      const isSecondary = secondaryZones.has(key);
                      const cls = `${styles.zoneBtn} ${isPrimary ? styles.primary : ""} ${isSecondary ? styles.secondary : ""}`;
                      return (
                        <button key={key} className={cls} onClick={() => handleZoneTap("right", z)}>
                          {z}
                        </button>
                      );
                    })}
              </div>
            </div>

            {/* Reserved-height helper text slot (no layout shift on appear/disappear) */}
            <div className={styles.plHelperSlot}>
              <div className={`${styles.plHelper} ${helperVisible ? styles.visible : ""}`}>Tap the teal highlighted button to start over.</div>
            </div>
          </div>

          {/* Age confirmation — disabled in Phase 1, enabled once Phase 2 begins */}
          <div className={`${styles.ageConfirm} ${ageHighlight ? styles.ageHighlight : ""}`}>
            <input type="checkbox" id="ageChk" checked={ageConfirmed || readOnly} disabled={!ageEnabled} onChange={(e) => setAgeConfirmed(e.target.checked)} />
            <label htmlFor="ageChk" style={{ opacity: ageEnabled ? 1 : 0.5 }}>
              I confirm I am 18 years of age or older
            </label>
          </div>

          {!readOnly && (
            <button className={styles.btnSafe} disabled={!canContinue} onClick={validateAndContinue} style={{ opacity: canContinue ? 1 : 0.5 }}>
              Continue
            </button>
          )}
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}
// ── Canvas helper ──
function roundRect(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
